import React, { useEffect, useState } from "react";
import { Loader, SearchCheck } from "lucide-react";
import { formatCurrencyAmount } from "../utils/formatCurrencyAmount";
import { usePositionExplanation } from "../hooks/useTreasury";

const FLAG_LABELS = {
  matches_shift: { text: "Matches the shift", className: "bg-red-100 text-red-800" },
  failed_or_review: { text: "Failed or in review", className: "bg-amber-100 text-amber-800" },
  large_share: { text: "Large share", className: "bg-gray-100 text-gray-700" },
  customer_conversion: { text: "Customer conversion (FX exposure)", className: "bg-blue-100 text-blue-800" },
  offset_by_partner: { text: "Paid from a partner balance (neutral)", className: "bg-green-100 text-green-800" },
};

const words = (value) => String(value ?? "").replace(/_/g, " ").toLowerCase();

const signed = (value, currency) => {
  const num = Number(value);
  if (!num) return formatCurrencyAmount(0, currency);
  return `${num > 0 ? "+" : "-"}${formatCurrencyAmount(Math.abs(num), currency)}`;
};

const tone = (value) => (Number(value) < 0 ? "text-red-600" : Number(value) > 0 ? "text-green-700" : "text-gray-500");

const when = (iso) => new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

const todayIso = () => new Date().toISOString().slice(0, 10);

const addDays = (iso, days) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

// "From" is the start of the window (blank = the previous saved snapshot).
const PRESETS = [
  { label: "Since previous snapshot", days: null },
  { label: "Yesterday", days: 1 },
  { label: "Last 7 days", days: 7 },
  { label: "Last 30 days", days: 30 },
];

const Heading = ({ children }) => (
  <p className="mt-5 mb-2 text-xs font-bold tracking-wider text-gray-500 uppercase">{children}</p>
);

const Table = ({ head, children }) => (
  <div className="overflow-x-auto rounded border border-gray-200 bg-white">
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-gray-50">
          {head.map(([label, align]) => (
            <th key={label} className={`px-3 py-2 text-xs font-semibold tracking-wider text-gray-500 uppercase ${align === "right" ? "text-right" : "text-left"}`}>
              {label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">{children}</tbody>
    </table>
  </div>
);

function ExplainBody({ data, currency }) {
  const { owed, held, delta } = data;
  return (
    <div>
      <p className="text-xs text-gray-500">
        Compared with {data.from.date}. Movements from the end of that day to{" "}
        {data.to.date === new Date().toISOString().slice(0, 10) ? "now" : `the end of ${data.to.date}`}.
      </p>
      {data.notes?.length > 0 && (
        <ul className="mt-1 space-y-0.5 text-xs text-amber-800">
          {data.notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      )}

      <ul className="mt-3 space-y-1.5 text-sm text-gray-900">
        {data.findings.map((line) => (
          <li key={line} className="flex gap-2">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 bg-[#013941]" />
            <span>{line}</span>
          </li>
        ))}
      </ul>

      <div className="grid gap-3 mt-4 sm:grid-cols-3">
        {[
          ["Owed change", delta.owed],
          ["Held change", delta.held],
          ["Gap change", delta.gap],
        ].map(([label, value]) => (
          <div key={label} className="p-3 bg-white rounded border border-gray-200">
            <p className="text-xs text-gray-500">{label}</p>
            <p className={`text-base font-semibold ${label === "Gap change" ? tone(value) : "text-gray-900"}`}>{signed(value, currency)}</p>
          </div>
        ))}
      </div>

      <Heading>Suspect transactions from our system</Heading>
      {owed.suspectsTotal > owed.suspects.length && (
        <p className="mb-2 text-xs text-gray-500">
          Showing the {owed.suspects.length} most relevant of {owed.suspectsTotal} movements. Narrow the range to see the rest.
        </p>
      )}
      {owed.suspects.length === 0 ? (
        <p className="text-sm text-gray-500">No merchant wallet transactions moved a balance in this window.</p>
      ) : (
        <Table head={[["When"], ["Business"], ["Type"], ["Status"], ["Reference"], ["Moved", "right"], ["Why flagged"]]}>
          {owed.suspects.map((t) => (
            <tr key={t.uuid} className={t.flags.length ? "bg-amber-50/40" : ""}>
              <td className="px-3 py-2 whitespace-nowrap text-gray-600">{when(t.createdAt)}</td>
              <td className="px-3 py-2 text-gray-900">
                {t.business ?? "-"}
                {t.isHouseMerchant && <span className="ml-1 text-xs text-gray-400">(house merchant)</span>}
              </td>
              <td className="px-3 py-2 capitalize text-gray-900">{words(t.serviceType)}</td>
              <td className="px-3 py-2 text-gray-700">{words(t.status)}</td>
              <td className="px-3 py-2 font-mono text-xs text-gray-600 break-all" title={t.description ?? undefined}>
                {t.reference ?? t.uuid}
                {t.counterpart && (
                  <span className="block font-sans text-xs text-gray-500">
                    other leg: {signed(t.counterpart.movement, t.counterpart.currency)}
                  </span>
                )}
              </td>
              <td className={`px-3 py-2 text-right whitespace-nowrap ${tone(t.movement)}`}>{signed(t.movement, currency)}</td>
              <td className="px-3 py-2">
                <div className="flex flex-wrap gap-1">
                  {t.flags.map((f) => (
                    <span key={f} className={`px-2 py-0.5 text-xs font-medium rounded-full ${FLAG_LABELS[f]?.className ?? ""}`}>
                      {FLAG_LABELS[f]?.text ?? f}
                    </span>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </Table>
      )}

      {owed.driftWallets.length > 0 && (
        <>
          <Heading>Balance changed without a transaction</Heading>
          <Table head={[["Business"], ["Wallet"], ["Balance", "right"], ["Last recorded", "right"], ["Difference", "right"]]}>
            {owed.driftWallets.map((w) => (
              <tr key={w.walletUuid}>
                <td className="px-3 py-2 text-gray-900">
                  {w.business ?? "-"}
                  {w.isHouseMerchant && <span className="ml-1 text-xs text-gray-400">(house merchant)</span>}
                </td>
                <td className="px-3 py-2 font-mono text-xs text-gray-600">{w.walletUuid}</td>
                <td className="px-3 py-2 text-right">{formatCurrencyAmount(w.balance, currency)}</td>
                <td className="px-3 py-2 text-right">{formatCurrencyAmount(w.lastRecordedBalance, currency)}</td>
                <td className={`px-3 py-2 text-right ${tone(w.difference)}`}>{signed(w.difference, currency)}</td>
              </tr>
            ))}
          </Table>
        </>
      )}

      {owed.buckets.length > 0 && (
        <>
          <Heading>Owed side, by kind of transaction</Heading>
          <Table head={[["Kind"], ["Status"], ["Count", "right"], ["Net", "right"]]}>
            {owed.buckets.map((b) => (
              <tr key={`${b.serviceType}-${b.status}`}>
                <td className="px-3 py-2 capitalize text-gray-900">{words(b.serviceType)}</td>
                <td className="px-3 py-2 text-gray-700">{words(b.status)}</td>
                <td className="px-3 py-2 text-right">{b.count}</td>
                <td className={`px-3 py-2 text-right ${tone(b.net)}`}>{signed(b.net, currency)}</td>
              </tr>
            ))}
            {Math.abs(owed.unexplained) > 0.005 && (
              <tr className="bg-gray-50">
                <td className="px-3 py-2 text-gray-600" colSpan={3}>Not covered by a recorded transaction</td>
                <td className={`px-3 py-2 text-right ${tone(owed.unexplained)}`}>{signed(owed.unexplained, currency)}</td>
              </tr>
            )}
          </Table>
        </>
      )}

      <Heading>Held side, by partner</Heading>
      {held.partners.length === 0 && Math.abs(held.unexplained) < 0.005 ? (
        <p className="text-sm text-gray-500">No partner balance changed in this window.</p>
      ) : (
        <Table head={[["Partner"], ["Before", "right"], ["After", "right"], ["Change", "right"]]}>
          {held.partners.map((p) => (
            <tr key={p.partner}>
              <td className="px-3 py-2 capitalize text-gray-900">{p.partner.toLowerCase()}</td>
              <td className="px-3 py-2 text-right">{p.before == null ? "-" : formatCurrencyAmount(p.before, currency)}</td>
              <td className="px-3 py-2 text-right">{p.after == null ? "-" : formatCurrencyAmount(p.after, currency)}</td>
              <td className={`px-3 py-2 text-right ${tone(p.delta)}`}>{signed(p.delta, currency)}</td>
            </tr>
          ))}
          {Math.abs(held.unexplained) > 0.005 && (
            <tr className="bg-gray-50">
              <td className="px-3 py-2 text-gray-600" colSpan={3} title="USD-only partners' balances are shared across currencies, so this part moves with exchange rates and with other currencies' deficits">
                USD allocation and rate effect
              </td>
              <td className={`px-3 py-2 text-right ${tone(held.unexplained)}`}>{signed(held.unexplained, currency)}</td>
            </tr>
          )}
        </Table>
      )}

      {held.conversions.length > 0 && (
        <>
          <Heading>Partner conversions in this window</Heading>
          <Table head={[["When"], ["Partner"], ["From", "right"], ["To", "right"], ["Status"], ["By"]]}>
            {held.conversions.map((c) => (
              <tr key={c.uuid}>
                <td className="px-3 py-2 whitespace-nowrap text-gray-600">{when(c.createdAt)}</td>
                <td className="px-3 py-2 capitalize text-gray-900">{c.partner.toLowerCase()}</td>
                <td className="px-3 py-2 text-right">{formatCurrencyAmount(c.fromAmount, c.fromCurrency)}</td>
                <td className="px-3 py-2 text-right">{formatCurrencyAmount(c.toAmount, c.toCurrency)}</td>
                <td className="px-3 py-2 text-gray-700">{c.status}</td>
                <td className="px-3 py-2 text-gray-600">{c.initiatedBy ?? "automatic"}</td>
              </tr>
            ))}
          </Table>
        </>
      )}

      {(held.receivedTopUps.length > 0 || held.failedSyncs.length > 0) && (
        <>
          <Heading>Other things that happened</Heading>
          <ul className="space-y-1 text-sm text-gray-700">
            {held.receivedTopUps.map((t) => (
              <li key={`${t.partner}-${t.receivedAt}`}>
                Top-up received at {t.partner.toLowerCase()}: {formatCurrencyAmount(t.amount, currency)} ({when(t.receivedAt)})
              </li>
            ))}
            {held.failedSyncs.map((f) => (
              <li key={`${f.partner}-${f.capturedAt}`} className="text-amber-800">
                Latest balance sync failed for {f.partner.toLowerCase()} ({when(f.capturedAt)}): {f.error}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

// "Why did this change?" for one currency row. Nothing is fetched until it is
// opened.
function PositionExplainPanel({ currency, date }) {
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState("");
  // The end of the window starts as the page date and can be changed here.
  const [to, setTo] = useState(date);
  useEffect(() => setTo(date), [date]);
  const { data, isLoading, isError, error } = usePositionExplanation({
    currency,
    date: to,
    compareTo: from || undefined,
    enabled: open,
  });

  const presetActive = (days) =>
    days === null ? !from : from === addDays(to, -days);

  return (
    <div className="mt-5 pt-4 border-t border-gray-200">
      <div className="flex flex-wrap gap-3 items-center">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
          className="flex gap-2 items-center px-4 py-2 text-sm font-bold text-white bg-[#013941] rounded-3xl transition-all hover:bg-[#012126]"
        >
          <SearchCheck className="w-4 h-4" />
          {open ? "Hide explanation" : "Why did this change?"}
        </button>
      </div>

      {open && (
        <div className="mt-4 space-y-3" onClick={(e) => e.stopPropagation()}>
          <div className="flex flex-wrap gap-3 items-center">
            <label className="flex gap-2 items-center text-sm text-gray-600">
              From
              <input
                type="date"
                value={from}
                max={addDays(to, -1)}
                onChange={(e) => setFrom(e.target.value)}
                className="px-3 py-1.5 text-sm rounded-3xl border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </label>
            <label className="flex gap-2 items-center text-sm text-gray-600">
              To
              <input
                type="date"
                value={to}
                max={todayIso()}
                onChange={(e) => e.target.value && setTo(e.target.value)}
                className="px-3 py-1.5 text-sm rounded-3xl border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </label>
            {!from && <span className="text-xs text-gray-400">(From: previous snapshot)</span>}
          </div>
          <div className="flex flex-wrap gap-2">
            {PRESETS.map(({ label, days }) => (
              <button
                key={label}
                type="button"
                onClick={() => setFrom(days === null ? "" : addDays(to, -days))}
                className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                  presetActive(days)
                    ? "text-white bg-[#013941] border-[#013941]"
                    : "text-gray-700 bg-white border-gray-200 hover:bg-gray-50"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {isLoading ? (
            <p className="flex gap-2 items-center text-sm text-gray-600">
              Working out what moved <Loader className="w-4 h-4 animate-spin" />
            </p>
          ) : isError ? (
            <p className="text-sm text-red-600">Could not load the explanation: {error?.message}</p>
          ) : !data?.available ? (
            <p className="text-sm text-gray-600">{data?.reason}</p>
          ) : (
            <ExplainBody data={data} currency={currency} />
          )}
        </div>
      )}
    </div>
  );
}

export default PositionExplainPanel;
